<template>
  <div class="summary">
    <FlightItineraryBook :is-edit="true" :flights="flights" :program="props.program" :is-lander="isLander" @edit="edit" />
    <div class="form-section">
      <h4>Fare</h4>
      <div v-for="(p, index) in prices" :key="index" class="fare" :class="{ on: fareSelectedIndex === index }" @click="fareSelect(index)">
        <div class="fare-inner">
          <div class="info">
            <div class="radiolabel" :class="{ on: fareSelectedIndex === index }">
              <div class="radio" />
              <label>{{ p.fare }}</label>
              <span v-if="p.fare === 'Basic Economy' && prices.length > 1" class="tag not-recommended">Not Recommended</span>
            </div>
            <div v-if="p.fare === 'Basic Economy'" class="det">
              <div class="bag">
                <span class="icon red">&#x274c;</span>
                <span>No included bags</span>
              </div>
              <div class="bag">
                <span class="icon red">&#x274c;</span>
                <span>No ticket changes</span>
              </div>
              <div class="bag">
                <span class="icon red">&#x274c;</span>
                <span>No refunds</span>
              </div>
            </div>
            <div v-else class="det">
              <div class="bag">
                <span v-if="p.bag_carry_on_count > 0" class="icon green">&#x2713;</span>
                <span v-else class="icon red">&#x274c;</span>
                <span>{{ p.bag_carry_on_count > 0 ? p.bag_carry_on_count : "No" }} free carry-on bag</span>
              </div>
              <div class="bag">
                <span v-if="p.bag_checked_count > 0" class="icon green">&#x2713;</span>
                <span v-else class="icon red">&#x274c;</span>
                <span>{{ p.bag_checked_count > 0 ? p.bag_checked_count : "No" }} free checked bag</span>
              </div>
              <div v-if="p.is_ticket_changeable" class="bag">
                <span class="icon green">&#x2713;</span>
                <span>Paid ticket changes</span>
              </div>
              <div v-else class="bag">
                <span class="icon red">&#x274c;</span>
                <span>No ticket changes</span>
              </div>
            </div>
          </div>
          <div class="pricing">
            <div class="amt">
              {{ priceLabel(p) }}
            </div>
            <div v-if="!props.isLander && p.is_policy" class="pol" :class="{ within: p.is_policy_within }">
              {{ policyLabel(p.is_policy_within) }}
            </div>
          </div>
        </div>
        <div v-if="shouldShowDebug" class="debug">{{ p.amount }} {{ p.fare }} ({{ p.source }}) {{ p.signature }}{{ p.is_codeshare ? " codeshare" : "" }}</div>
      </div>
    </div>
    <div v-if="!isLander" id="navigation">
      <button @click="next">Next Step</button>
      <a @click="back">Back</a>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, computed, onMounted } from "vue";
import FlightItineraryBook from "./TripNewBookSummaryFlightBook.vue";
import { formatPrice } from "../utils/money";

// Props
const props = defineProps({
  flights: Object,
  program: Object,
  isSavedRoute: Boolean,
  isLander: Boolean,
});

const userIsExpert = inject("userIsExpert");
const shouldShowDebug = computed(() => userIsExpert.value && prices.value.length > 0);

// Emits
const emit = defineEmits(["update:isSavedFlights", "update:isSavedRoute", "update:flights", "update:legIndex", "complete", "scroll"]);

// State
const fareSelectedIndex = ref(0);

// Computed property for prices
const prices = computed(() => {
  const seenKeys = [];
  const uniquePrices = [];

  // Guard against empty or invalid route
  if (!props.flights?.route?.length) return uniquePrices;

  const lastLeg = props.flights.route[props.flights.route.length - 1];
  if (!lastLeg?.ideal?.prices) return uniquePrices;

  const priceList = lastLeg.ideal.prices;

  // Copy prices to avoid mutating props (which triggers reactive loops)
  const normalized = priceList
    .filter(p => p.is_option)
    .map(p => ({ ...p }));

  // Merge bag data from equivalent fares
  normalized.forEach(p => {
    if (p.is_bag_data && !p.bag_carry_on_count && !p.bag_checked_count) {
      const pp = normalized.find(pp => pp.is_bag_data && pp.bag_carry_on_count && pp.fare === p.fare);
      p.bag_carry_on_count = pp ? pp.bag_carry_on_count : 1;
      p.bag_checked_count = pp ? pp.bag_checked_count : 0;
    }
  });

  // Deduplicate by fare name, preferring non-codeshare prices
  normalized.forEach(p => {
    const key = p.fare;
    const existingIdx = seenKeys.indexOf(key);
    if (existingIdx === -1) {
      seenKeys.push(key);
      uniquePrices.push(p);
    } else if (uniquePrices[existingIdx].is_codeshare && !p.is_codeshare) {
      uniquePrices[existingIdx] = p;
    }
  });

  return uniquePrices;
});
// Lifecycle hook
onMounted(() => {
  // Pre-select the first non-basic fare if available, otherwise fall back to the first fare
  const preferredIndex = prices.value.findIndex(p => p.fare !== "Basic Economy");
  fareSelect(preferredIndex >= 0 ? preferredIndex : 0);
});

// Methods
const back = () => {
  emit("update:isSavedFlights", false);
  emit("update:isSavedRoute", false);
};

const edit = index => {
  const newFlights = { ...props.flights };
  if (!props.isLander) {
    newFlights.route?.forEach((leg, i) => {
      if (i >= index) leg.ideal = {};
    });
  }
  emit("update:flights", newFlights);
  emit("update:legIndex", index);
  emit("update:isSavedFlights", false);
};

const fareSelect = i => {
  fareSelectedIndex.value = i;

  const price = prices.value[i];
  if (!price) return;

  const newFlights = { ...props.flights };

  // Set price_selected on all legs (shared fare applies to entire itinerary)
  newFlights.route?.forEach(leg => {
    if (leg.ideal) {
      leg.ideal.price_selected = price;
    }
  });

  emit("update:flights", newFlights);
};

const next = () => {
  const newFlights = { ...props.flights };
  emit("update:flights", newFlights);
  emit("complete");
  emit("scroll");
};

const policyLabel = is => (is ? "In Policy" : "Out of Policy");

// p.amount is canonical USD; formatPrice consults the org currency captured at search time.
const priceLabel = p => formatPrice(p.amount);
</script>

<style lang="less">
#flights {
  .summary {
    .fare {
      background: @lighter;
      border: 0.0625rem solid @border;
      border-radius: 1rem;
      margin-bottom: 1rem;
      padding: 1rem;

      &:hover {
        border-color: @primary;
      }

      .info {
        flex: 1;

        .features {
          margin: 1rem 1rem 0;
        }
      }

      .pricing {
        margin-top: 1rem;
      }

      .pol {
        color: @secondary;

        &.within {
          color: @green;
        }
      }

      .det {
        margin-top: 1rem;
        padding-left: 1.625rem;

        .icon {
          margin-right: 0.5rem;
        }
      }

      .debug {
        margin-top: 1rem;
        overflow-x: auto;
        padding: 1rem;
        white-space: nowrap;
      }

      .radiolabel:has(.not-recommended) {
        align-items: center;
      }

      .tag.not-recommended {
        background-color: @lightred;
        border: 0.0625rem solid @secondary;
        display: inline-block;
        border-radius: 0.5rem;
        color: @secondary;
        font-size: 0.75rem;
        padding: 0.125rem 0.5rem;
      }

      &.on {
        border-color: @primary;
      }
    }
  }
}

@media screen and (min-width: 60em) {
  #flights {
    .summary {
      .fare {
        cursor: pointer;

        .fare-inner {
          align-items: center;
          display: flex;
        }

        .pricing {
          margin-top: 0;
          text-align: right;
        }
      }
    }
  }
}
</style>
